import { ApiError } from "@softmax/observatory-client/transport";

import type { PublicPlayShell } from "../../lib/types";
import { type PlayApi } from "../../data/play-api";

import type { Recommended } from "../types";
import { playName } from "../one-pager-model";

const recommended = 3;

export async function readRecommended({
  api,
  shell,
}: {
  readonly api: PlayApi;
  readonly shell: PublicPlayShell;
}): Promise<Recommended[]> {
  const { league, divisionId } = shell;
  const fullLeague = await api.league(league.id);
  const ids = [
    ...(fullLeague?.seed_policy_version_ids ?? []),
    ...(fullLeague?.filler_policy_version_ids ?? []),
  ].slice(0, recommended);
  const owned = (
    await Promise.all(ids.map((id) => api.policyVersionDetail(id)))
  ).filter((p) => p !== null);

  if (owned.length > 0)
    return owned.map((p) => ({
      label: `${p.name}:v${p.version}`,
      by: null,
      source: "starter" as const,
      id: p.id,
    }));

  // The leaderboard is only a fallback; it cannot delay the league's own starters.
  const leaderboard = divisionId
    ? await api.divisionLeaderboard({
        divisionId,
        anonymous: await api.leagueIsPublic(league.id),
      })
    : null;

  if (leaderboard?.status === "unavailable")
    throw new ApiError("Recommendations unavailable.", 503);

  const entries =
    leaderboard?.status === "published" ? leaderboard.summary.entries : [];
  const programs: Recommended[] = [];

  for (const entry of entries) {
    const label = entry.policy_label;

    if (!label) continue;

    programs.push({
      label,
      by: entry.player_name?.replace(playName, "") ?? null,
      source: "top",
      id: null,
    });

    if (programs.length === recommended) break;
  }

  return programs;
}
